import React, { useEffect, useRef, useState } from 'react'
import { useTheme } from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import Text from '../../Text'
import hipersendService from '../../../services/hipersend'
import { TEMPLATE_CATEGORY_LABELS } from '../../../libs/metaTemplates'
import { fmtTelefone, fmtBRL, fmtPct } from '../../../libs/relatorioPdf/documentos'
import { corLegivel } from '../../../libs/corLegivel'

// Mesmas cores do selo de categoria da tabela (TemplateCategoryBadge): o
// "Marketing" roxo daqui é o mesmo roxo da linha lá embaixo.
export const COR_CATEGORIA = {
  MARKETING: '#7c3aed',
  MARKETING_LITE: '#7c3aed',
  UTILITY: '#2563eb',
  AUTHENTICATION: '#b45309',
  OTP: '#b45309',
  SERVICE: '#64748b',
}

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR')

function Cartao({ children }) {
  const theme = useTheme()
  return (
    <div
      className="rounded-[14px] px-4 py-3.5 min-w-0"
      // cor explícita: os <span>/<li> soltos herdam daqui, e não do body (escuro)
      style={{
        border: theme.isDark ? '1px solid #334155' : '1px solid #e2e8f0',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }}
    >
      {children}
    </div>
  )
}

function Cabecalho({ titulo, acao, onAcao }) {
  const theme = useTheme()
  return (
    <div className="flex items-center justify-between gap-3">
      <Text className="text-[13.5px] font-extrabold">{titulo}</Text>
      {onAcao && (
        <button
          type="button"
          onClick={onAcao}
          className="flex items-center gap-1 text-xs font-bold"
          style={{ color: corLegivel(theme.palette.primary.main, theme.palette.background.paper) }}
        >
          <FileDownloadOutlinedIcon style={{ fontSize: 15 }} />
          {acao}
        </button>
      )}
    </div>
  )
}

function Esqueleto({ linhas = 4 }) {
  return (
    <div className="mt-3 grid gap-2.5">
      {Array.from({ length: linhas }).map((_, i) => (
        <div key={i} className="h-4 rounded bg-current opacity-10 animate-pulse" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  )
}

function RankingDeModelos({ dados, carregando, onExportar }) {
  const theme = useTheme()
  const modelos = (dados?.modelos || []).slice(0, 5)
  const maior = Math.max(1, ...modelos.map((m) => m.enviadas))
  const temCusto = modelos.some((m) => m.custo !== null && m.custo !== undefined)
  const temCustoMeta = !temCusto && modelos.some((m) => m.custoMeta)

  return (
    <Cartao>
      <Cabecalho titulo="Modelos mais disparados" acao="Exportar ranking" onAcao={modelos.length ? onExportar : null} />
      {carregando && !dados ? (
        <Esqueleto />
      ) : modelos.length === 0 ? (
        <Text className="text-xs mt-3 opacity-60">Nenhum modelo enviado no período.</Text>
      ) : (
        <>
          <ol className="mt-3 grid gap-2.5">
            {modelos.map((m) => {
              const cat = String(m.categoria || '').toUpperCase()
              const cor = COR_CATEGORIA[cat] || '#64748b'
              return (
                <li key={m.nome} className="grid items-center gap-x-3 gap-y-1 text-xs" style={{ gridTemplateColumns: 'minmax(0,1fr) 60px 76px' }}>
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate font-semibold">{m.nome}</span>
                    {TEMPLATE_CATEGORY_LABELS[cat] && (
                      <span
                        className="shrink-0 rounded px-1.5 py-[1px] text-[9.5px] font-extrabold uppercase tracking-wide"
                        style={{ color: corLegivel(cor, theme.palette.background.paper), background: `${cor}1f` }}
                      >
                        {TEMPLATE_CATEGORY_LABELS[cat]}
                      </span>
                    )}
                  </span>
                  <span className="text-right font-extrabold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(m.enviadas)}</span>
                  <span className="text-right opacity-60" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {temCusto ? fmtBRL(m.custo) : temCustoMeta && m.custoMeta ? fmtBRL(m.custoMeta.total) : m.entregues ? `${fmtPct(m.lidas / m.entregues, 0)} lidas` : '—'}
                  </span>
                  <span className="col-span-3 h-[5px] rounded-full overflow-hidden" style={{ background: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}>
                    <i
                      className="block h-full rounded-full animate-fade-right animate-duration-700"
                      style={{ width: `${(m.enviadas / maior) * 100}%`, background: theme.palette.primary.main }}
                    />
                  </span>
                </li>
              )
            })}
          </ol>
          <Text className="text-[11px] opacity-60 mt-2.5">
            {temCusto
              ? 'Custo = o que foi debitado do seu saldo por modelo no período. Mensagens com erro não entram.'
              : temCustoMeta
                ? 'Custo estimado na Meta, pela tabela oficial — pago direto no seu cartão cadastrado lá.'
                : 'Percentual de leitura entre as mensagens que chegaram.'}
          </Text>
        </>
      )}
    </Cartao>
  )
}

function NumerosInexistentes({ dados, carregando, onExportar }) {
  const theme = useTheme()
  const totais = dados?.totais
  const numeros = (dados?.numeros || []).slice(0, 3)
  const base = dados?.tentativasPeriodo?.tentativas || 0
  const peso = base ? (totais?.tentativas || 0) / base : 0

  return (
    <Cartao>
      <Cabecalho titulo="Números sem WhatsApp" acao="Exportar lista" onAcao={totais?.numeros ? onExportar : null} />
      {carregando && !dados ? (
        <Esqueleto linhas={3} />
      ) : !totais?.numeros ? (
        <Text className="text-xs mt-3 opacity-60">Nenhum número sem WhatsApp no período. Sua base está em dia.</Text>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mt-2.5">
            <Text
              className="text-[30px] font-black leading-none"
              style={{ color: corLegivel('#eb3c3c', theme.palette.background.paper, 3), fontVariantNumeric: 'tabular-nums' }}
            >
              {fmt(totais.numeros)}
            </Text>
            <Text className="text-xs opacity-60">
              {totais.numeros === 1 ? 'número' : 'números'} · {fmt(totais.tentativas)} tentativas ({fmtPct(peso)})
            </Text>
          </div>
          <Text className="text-[11.5px] opacity-60 mt-2 leading-snug">
            A Meta recusou porque esses números não têm WhatsApp (erro 131026). Nenhuma foi cobrada. Atualize o cadastro para não repetir.
          </Text>
          <ul className="mt-2.5 grid gap-1.5">
            {numeros.map((n) => (
              <li
                key={n.telefone}
                className="flex justify-between rounded-lg px-2.5 py-1.5 text-xs"
                style={{ background: theme.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)' }}
              >
                <span className="font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtTelefone(n.telefone)}</span>
                <span className="opacity-50">{n.tentativas > 1 ? `falhou ${n.tentativas}×` : 'falhou 1×'}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Cartao>
  )
}

// Ranking de modelos e números sem WhatsApp do período APLICADO. Só API
// Oficial: é lá que existe nome de modelo e o aviso da Meta de número
// inexistente (na não oficial ficam ocultos — decisão de produto).
export default function AnalisesDoPeriodo({ api, secret, range, metaOficial, onExportar }) {
  const [modelos, setModelos] = useState(null)
  const [inexistentes, setInexistentes] = useState(null)
  const [carregando, setCarregando] = useState(false)
  const req = useRef(0)

  useEffect(() => {
    if (!metaOficial || !api || !secret || !range || range.incomplete || !range.dateBegin) return
    const id = ++req.current
    setCarregando(true)
    const periodo = { dateBegin: range.dateBegin, dateEnd: range.dateEnd }
    Promise.all([
      hipersendService.notificacoesInteligentes.getRelatorioModelos(api, secret, periodo).catch(() => null),
      hipersendService.notificacoesInteligentes.getRelatorioInexistentes(api, secret, periodo).catch(() => null),
    ]).then(([m, i]) => {
      // trocar de período rápido dispara duas buscas: só a última pinta
      if (id !== req.current) return
      setModelos(m)
      setInexistentes(i)
      setCarregando(false)
    })
  }, [api, secret, metaOficial, range?.dateBegin, range?.dateEnd]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!metaOficial) return null
  // Master sem as rotas novas (ou fora do ar): some em silêncio, como o consumo.
  if (!carregando && modelos === null && inexistentes === null) return null

  return (
    <div className="mt-3 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-3">
      <RankingDeModelos dados={modelos} carregando={carregando} onExportar={(e) => onExportar('modelos', e && e.currentTarget)} />
      <NumerosInexistentes dados={inexistentes} carregando={carregando} onExportar={(e) => onExportar('inexistentes', e && e.currentTarget)} />
    </div>
  )
}
